import React, { CSSProperties, JSXElementConstructor, useCallback, useEffect, useState } from 'react';

import isEqual from 'lodash/isEqual';
import ReactDOM from 'react-dom';

import { useHeaderFixedHeight } from '@lumapps/utils/hooks/useHeaderHeight';
import { useWindowResize } from '@lumapps/utils/hooks/useWindowResize';
import { useWindowScroll } from '@lumapps/utils/hooks/useWindowScroll';

// Props injected by useStickyCell
type InjectedProps = { style?: CSSProperties };

type Options<P> = {
    /** Toggle hook on/off. */
    enable?: boolean;
    /** React component to wrap (must support ref forwarding) */
    component: JSXElementConstructor<P & InjectedProps>;
    /** React component props provided by the user. */
    props: P & InjectedProps;
};

const DEFAULT_STICKY_CELL_MARGIN_BOTTOM = 12;

/**
 * Wraps an element with state and logic to get it to stick at the top of the page after reaching past it using scroll.
 * The element sticks under the top bar (=header).
 *
 * When element is sticky and gets out of viewport:
 * => Put original element in hidden visibility
 * => Clone the element in a portal (with fixed position and same left/right position as original element)
 *
 * Updates on window scroll.
 * Updates cloned element left/right position on window resize.
 */
export function useStickyCell<P>({ enable, component: Component, props }: Options<P>) {
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const [style, setStyle] = useState<CSSProperties>();

    // Use size of the header height as top offset.
    const topOffset = useHeaderFixedHeight();

    const updateStyle = useCallback(() => {
        if (!enable || !element) {
            return;
        }
        const cell = element.getBoundingClientRect();

        const cellMarginBottom = Number(
            window.getComputedStyle(element)?.marginBottom ?? DEFAULT_STICKY_CELL_MARGIN_BOTTOM,
        );

        // Not in sticky mode
        if (cell.top >= topOffset) {
            setStyle(undefined);
            return;
        }

        // Use the height of the wrapper as height of the cell to avoid taking into account the empty space under the widgets
        const cellHeight = element.children[0].getBoundingClientRect().height;

        // Interior height of the window
        const windowHeight = window.innerHeight;

        const body = document.body.getBoundingClientRect();

        // Footer Y position OR Y position of the end of the body if no footer
        const footerTop: number =
            document.querySelector('footer[role=contentinfo]')?.getBoundingClientRect().top ?? body.height + body.top;

        const isFooterVisible = footerTop <= windowHeight;

        // If the footer is visible and there is not enough space remaining to display the whole sticky cell
        // OR
        // If the sticky cell is taller than the available display space and the footer comes right under it
        // THEN
        // We align the sticky cell's bottom to the footer top (end of body when there is no footer)
        // to make it appear like the footer/end of the body is pushing the sticky cell upwards
        const verticalPosition =
            (isFooterVisible && windowHeight - footerTop > topOffset + cellMarginBottom) ||
            footerTop - topOffset <= cellHeight
                ? { top: 'initial', bottom: windowHeight - footerTop }
                : { top: topOffset, bottom: 'initial' };

        const newStyle: CSSProperties = {
            position: 'fixed',
            left: cell.left,
            right: document.body.clientWidth - cell.width - cell.left,
            ...verticalPosition,
        };

        // Only update if the style has changed
        setStyle((prevStyle) => (isEqual(newStyle, prevStyle) ? prevStyle : newStyle));
    }, [element, enable, topOffset]);
    // On mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(updateStyle, [element]);
    // On element resize.
    useEffect(() => {
        if (!element) {
            return undefined;
        }
        const observer = new ResizeObserver(updateStyle);
        observer.observe(element);
        return () => observer.disconnect();
    }, [element, updateStyle]);
    // On window resize.
    useWindowResize(updateStyle);
    // On scroll.
    useWindowScroll(updateStyle);

    return enable && style ? (
        <>
            <Component {...props} ref={setElement} style={{ ...props.style, visibility: 'hidden' }} />
            {ReactDOM.createPortal(<Component {...props} style={{ ...props.style, ...style }} />, document.body)}
        </>
    ) : (
        <Component {...props} ref={setElement} />
    );
}
